import useSWR from "swr";
import { useSession } from "@/hooks/use-session";

export interface Hunt {
  id: string;
  event_id: string | null;
  day_number: number;
  name: string;
  prize_description: string;
  is_current: boolean;
}

export interface HuntItem {
  id: string;
  hunt_id: string;
  name: string;
  description: string | null;
  order_index: number;
}

export interface Completion {
  id: string;
  hunt_item_id: string;
  patent_id: string;
  input_image_url?: string | null;
  patent_cdn_path?: string | null;
}

export interface HuntData {
  hunt: Hunt | null;
  items: HuntItem[];
  completions: Completion[];
  hasEnteredDrawing: boolean;
}

async function fetcher(url: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Hunt fetch failed (${res.status})`);
  return res.json();
}

export function useHunt() {
  const { session, isLoading: sessionLoading } = useSession();
  // Only fetch hunt after we know there is a session. Calling /api/hunt without a
  // session returns empty hunt data; SWR cached that and briefly showed "No Active
  // Hunt" after registration before the real hunt loaded.
  const shouldFetch = !!session && !sessionLoading;
  const { data, error, isLoading: swrLoading, mutate } = useSWR<HuntData>(
    shouldFetch ? "/api/hunt" : null,
    fetcher,
    { shouldRetryOnError: false },
  );

  const isLoading = sessionLoading || (shouldFetch && swrLoading);

  return {
    hunt: data?.hunt ?? null,
    items: data?.items ?? [],
    completions: data?.completions ?? [],
    hasEnteredDrawing: data?.hasEnteredDrawing ?? false,
    isLoading,
    error,
    mutate,
  };
}
